class PrimerryCollectionPage extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange);
    this.addEventListener('submit', this.handleSubmit);
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleKeydown);
    requestAnimationFrame(() => requestAnimationFrame(() => this.scrollActiveTabIntoView()));
    this.querySelectorAll('dialog').forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) this.closeDialog(dialog);
      });
      dialog.addEventListener('close', () => document.documentElement.classList.remove('primerry-collection-modal-open'));
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('submit', this.handleSubmit);
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleClick(event) {
    const quickOpen = event.target.closest('[data-primerry-quick-open]');
    if (quickOpen && this.contains(quickOpen)) {
      this.openDialog(quickOpen.dataset.primerryQuickOpen);
      return;
    }
    const quickClose = event.target.closest('[data-pdp-quick-close]');
    if (quickClose && this.contains(quickClose)) {
      this.closeDialog(quickClose.closest('dialog'));
      return;
    }
    const thumb = event.target.closest('[data-pdp-quick-thumb]');
    if (thumb && this.contains(thumb)) {
      const dialog = thumb.closest('[data-pdp-quick-dialog]');
      const image = dialog?.querySelector('.primerry-pdp-qv__main-image-element');
      if (image && thumb.dataset.imageSrc) {
        image.src = thumb.dataset.imageSrc;
        image.srcset = '';
        dialog.querySelectorAll('[data-pdp-quick-thumb]').forEach((item) => item.classList.toggle('is-active', item === thumb));
      }
      return;
    }
    if (event.target.closest('[data-collection-filter-open]')) {
      const dialog = this.querySelector('[data-collection-filter-dialog]');
      if (dialog && !dialog.open) {
        this.closeSortMenu();
        this.configureFilterGroups();
        dialog.showModal();
        document.documentElement.classList.add('primerry-collection-modal-open');
      }
      return;
    }
    const filterClose = event.target.closest('[data-collection-filter-close]');
    if (filterClose) {
      this.closeDialog(filterClose.closest('dialog'));
      return;
    }
    const sortToggle = event.target.closest('[data-collection-sort-toggle]');
    if (sortToggle && this.contains(sortToggle)) {
      event.stopPropagation();
      this.toggleSortMenu(sortToggle);
    }
  }

  handleChange(event) {
    const pricePreset = event.target.closest('[data-collection-price-preset]');
    if (pricePreset && this.contains(pricePreset) && pricePreset.checked) {
      this.querySelectorAll('[data-collection-price-preset]').forEach((input) => {
        if (input !== pricePreset) input.checked = false;
      });
      return;
    }
    const sort = event.target.closest('[data-collection-sort-option]');
    if (!sort || !this.contains(sort)) return;
    this.closeSortMenu();
    sort.form?.requestSubmit();
  }

  handleDocumentClick(event) {
    if (!event.target.closest('[data-collection-sort-menu]') && !event.target.closest('[data-collection-sort-toggle]')) {
      this.closeSortMenu();
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;
    const menu = this.querySelector('[data-collection-sort-menu]');
    if (menu && !menu.hidden) {
      event.preventDefault();
      this.closeSortMenu(true);
    }
  }

  toggleSortMenu(toggle) {
    const menu = this.querySelector('[data-collection-sort-menu]');
    if (!menu) return;
    const willOpen = menu.hidden;
    menu.hidden = !willOpen;
    toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    if (willOpen) menu.querySelector('input:checked')?.focus({ preventScroll: true });
  }

  closeSortMenu(restoreFocus = false) {
    const menu = this.querySelector('[data-collection-sort-menu]');
    const toggle = this.querySelector('[data-collection-sort-toggle]');
    if (!menu || menu.hidden) return;
    menu.hidden = true;
    toggle?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) toggle?.focus({ preventScroll: true });
  }

  scrollActiveTabIntoView() {
    const tabs = this.querySelector('.primerry-collection__tabs');
    const activeTab = tabs?.querySelector('.primerry-collection__tab.is-active');
    if (!tabs || !activeTab || tabs.scrollWidth <= tabs.clientWidth) return;
    const tabsRect = tabs.getBoundingClientRect();
    const activeRect = activeTab.getBoundingClientRect();
    const centeredPosition = tabs.scrollLeft
      + activeRect.left
      - tabsRect.left
      - ((tabs.clientWidth - activeRect.width) / 2);
    const maximumPosition = tabs.scrollWidth - tabs.clientWidth;
    tabs.scrollLeft = Math.max(0, Math.min(centeredPosition, maximumPosition));
  }

  configureFilterGroups() {
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    this.querySelectorAll('[data-collection-filter-group]').forEach((group, index) => {
      group.open = mobile ? index === 0 : true;
    });
  }

  openDialog(id) {
    const dialog = this.querySelector(`#${CSS.escape(id)}`);
    if (!dialog || dialog.open) return;
    dialog.showModal();
    document.documentElement.classList.add('primerry-collection-modal-open');
  }

  closeDialog(dialog) {
    if (dialog?.open) dialog.close();
    document.documentElement.classList.remove('primerry-collection-modal-open');
  }

  async handleSubmit(event) {
    const form = event.target.closest('[data-pdp-qv-form]');
    if (!form || !this.contains(form)) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('[data-pdp-error]');
    if (!button || button.dataset.loading === 'true') return;
    button.dataset.loading = 'true';
    button.disabled = true;
    button.textContent = 'adding…';
    if (error) error.hidden = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
        credentials: 'same-origin',
      });
      const item = await response.json();
      if (!response.ok) throw new Error(item.description || item.message || 'Unable to add this product.');
      const [cart, renderedSections] = await Promise.all([
        fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((result) => result.json()),
        fetch(`${window.location.pathname}?sections=cart-drawer-section`, { credentials: 'same-origin' }).then((result) => result.json()),
      ]);
      const drawerHtml = renderedSections['cart-drawer-section'];
      const currentDrawer = document.querySelector('#shopify-section-cart-drawer-section');
      if (drawerHtml && currentDrawer) {
        const template = document.createElement('template');
        template.innerHTML = drawerHtml.trim();
        const replacement = template.content.firstElementChild;
        if (replacement) currentDrawer.replaceWith(replacement);
      }
      document.querySelectorAll('.primerry-header__cart-count').forEach((count) => {
        count.textContent = Math.min(cart.item_count, 99).toString();
        count.classList.toggle('primerry-header__cart-count--empty', cart.item_count === 0);
      });
      this.closeDialog(form.closest('dialog'));
      requestAnimationFrame(() => document.querySelector('.primerry-header__cart')?.click());
    } catch (submitError) {
      if (error) {
        error.textContent = submitError instanceof Error ? submitError.message : 'Unable to add this product.';
        error.hidden = false;
      }
    } finally {
      button.dataset.loading = 'false';
      button.disabled = false;
      button.textContent = button.dataset.defaultLabel || 'add to cart';
    }
  }
}

if (!customElements.get('primerry-collection-page')) {
  customElements.define('primerry-collection-page', PrimerryCollectionPage);
}
